import { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Card, Text, Button } from '@bancobarigui/barihaus';

import { inspectionAssignmentService } from '@/services/inspectionAssignment.service';

interface InspectorRejectModalProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  onSuccess?: () => void;
}

export function InspectorRejectModal({
  open,
  onClose,
  assignmentId,
  onSuccess
}: InspectorRejectModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: { rejectionNotes: string }) => {
    setSubmitting(true);
    try {
      await inspectionAssignmentService.inspectorReject(assignmentId, {
        rejectionNotes: values.rejectionNotes
      });

      message.success('Inspeção recusada com sucesso!');
      form.resetFields();
      onSuccess?.();
      onClose();
    } catch {
      message.error('Erro ao recusar inspeção');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <CloseCircleOutlined className="text-red-500" />
          <Text strong>Recusar Inspeção</Text>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className="py-4">
        <Card className="mb-6 bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <CloseCircleOutlined className="text-red-500 mt-1" />
            <div>
              <Text strong className="text-red-900 block mb-1">
                Recusar Inspeção
              </Text>
              <Text className="text-red-700 text-sm">
                Ao recusar esta inspeção, ela será redirecionada para outro inspetor. 
                É obrigatório informar o motivo da recusa.
              </Text>
            </div>
          </div>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="rejectionNotes"
            label="Motivo da Recusa"
            rules={[
              { required: true, message: 'Por favor, informe o motivo da recusa' },
              { min: 10, message: 'O motivo deve ter pelo menos 10 caracteres' }
            ]}
            extra="Explique detalhadamente o motivo da recusa da inspeção"
          >
            <Input.TextArea
              placeholder="Ex: Não tenho disponibilidade para realizar a inspeção nesta data. Por favor, redirecionar para outro inspetor."
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item className="mb-0 pt-4">
            <div className="flex gap-3 justify-end">
              <Button
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                buttonColor="primary"
                htmlType="submit"
                loading={submitting}
              >
                Recusar Inspeção
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}